import { defineStore } from "pinia";
import axios from "axios";

import {
  getFilteredObjectByValuePath,
  getObjectElementByValuePath,
  objectByString,
} from "@/pages/product_card/tools/object";
import {
  isActiveColor,
  isActiveSize,
  isShowSizeMap,
} from "@/pages/product_card/tools/properties";
import {
  filterObject,
  filterSameObject,
} from "@/pages/product_card/tools/filter";
import { setOfferUrl } from "@/pages/product_card/tools/browser";

export const useProductStore = defineStore("catalog", {
  state: () => {
    return {
      products: [],
      offers: [],
      product: {
        id: "",
        name: "",
        code: "",
        media: {},
        props: {
          brand: {},
          model: {},
          sex: {},
          ncode: {},
          reserve_only_available: {},
          vid_tovara: {},
          lineyka: {},
        },
        documents: {},
        features: {},
        offers: {},
        section: {},
        sizeMap: {
          rows: {},
        },
        sizePicture: {},
        mkt: {},
        selected: false,
        service: {},
        shipping: {},
        rich_content: "",
        richButtonColorBg: "",
        richButtonColorText: "",
      },
      delivery: {
        attempts: 12,
        result: {},
        intervalId: 0,
      },
      wishList: [],
      selectedOfferId: new URLSearchParams(window.location.search).get("offer"),
      sizeRowId: 0,
      basket: {},
      loadingProcess: {
        basket: false,
        oneclick: false,
        preload: true,
      },
      errors: {
        basket: false,
      },
      installments: {
        tinkoff: {},
      },
      mainSlider: {
        activeSlideIndex: 0,
      },
      content: {},
      isSelected: {},
      special: [],
    };
  },
  actions: {
    fillSpecial: function (data) {
      this.special = data;
    },
    fillProductInfo: function (data) {
      Object.values(data).forEach((item) => {
        let product = {};
        product.id = objectByString(item, "fields.id");
        product.name = objectByString(item, "fields.name");
        product.code = objectByString(item, "fields.code");
        product.text = objectByString(item, "fields.detail_text");
        product.media = item?.media;
        product.productCode = objectByString(
          item,
          "properties.kod_tovara.value"
        );
        product.rich_content = objectByString(item, "properties.rich_content");

        let richButtonColor = objectByString(
          item,
          "properties.rich_button_color"
        )?.value.split(":");
        if (richButtonColor !== undefined) {
          product.richButtonColorBg = richButtonColor[0];
          product.richButtonColorText = richButtonColor[1];
        }
        product.props = {};
        product.props.vid_tovara = objectByString(
          item,
          "properties.vid_tovara_1.value"
        );
        product.props.lineyka = objectByString(
          item,
          "properties.lineyka.xml_id"
        );
        product.props.brand = objectByString(item, "properties.brend.value");
        product.props.model = objectByString(item, "properties.model.value");
        product.props.sex = objectByString(item, "properties.pol.value");
        product.props.ncode = objectByString(item, "properties.ncode.value");
        product.props.reserve_only_available = objectByString(
          item,
          "properties.reserve_only_available.value"
        );
        product.props.utp_items = objectByString(item, "properties.utp_items");
        product.props.ipra = objectByString(item, "properties.show_ipra.value");
        product.props.can_video_call = objectByString(
          item,
          "properties.can_video_call.value"
        );
        product.props.fcc = objectByString(item, "properties.fcc_available.id");
        product.props.badges = objectByString(item, "properties.badges");
        product.props.compression = {};
        product.props.compression.img = objectByString(
          item,
          "properties.compression_class_img.value"
        );
        product.props.compression.img2 = objectByString(
          item,
          "properties.compression_class_img_hour.value"
        );

        product.documents = item?.documents;
        product.selected = item.selected;
        product.property_table = item?.property_table;
        product.color = item?.color;

        product.section = objectByString(item, "fields.iblock_section");
        product.sizePicture = objectByString(item, "properties.size_picture");
        product.service = objectByString(item, "properties.service_order");
        product.shipping = objectByString(
          item,
          "properties.shipping_restriction"
        );

        product.mkt = objectByString(item, "mkt");

        if (item?.size_map?.rows !== undefined) {
          product.sizeMap = item?.size_map;
        }

        this.products.push(product);
      });
    },
    fillOffersInfo: function (data) {
      this.offers = data;
    },
    isActiveColor: function (productId) {
      return isActiveColor(productId, this.getSelectedProduct);
    },
    isActiveSize: function (sizeId) {
      return (
        isActiveSize(sizeId, this.getSelectedOffer) &&
        (this.isSelected.size || this.selectedOfferId != null)
      );
    },
    isActiveParam: function (paramName, paramId) {
      if (this.getSelectedOffer === undefined) {
        return false;
      }

      return (
        Number(
          objectByString(
            this.getSelectedOffer,
            "props." + paramName + ".value.id"
          )
        ) === Number(paramId) &&
        (this.isSelected[paramName] || this.selectedOfferId != null)
      );
    },
    isAllParamSelected: function () {
      let allParamSelected = true;

      if (this.selectedOfferId == null) {
        for (let key in this.isSelected) {
          if (!this.isSelected[key]) {
            allParamSelected = false;
            break;
          }
        }
      }

      return allParamSelected;
    },
    setProductById(id) {
      Object.keys(this.products).forEach((key) => {
        this.products[key]["selected"] =
          Number(this.products[key]["id"]) === Number(id);
      });
    },
    setColor(productId) {
      if (undefined === productId) {
        return false;
      }
      this.clearActiveOffer();

      this.setProductById(productId);
      const offersObj = this.getOffersByProductId(productId);

      if (Object.keys(offersObj).length > 0) {
        this.selectOfferById(Object.values(offersObj)[0]["id"]);
      }
    },
    clearActiveOffer() {
      Object.keys(this.offers).forEach((key) => {
        this.offers[key]["selected"] = false;
      });
    },
    getOffersByProductId(productId) {
      return getFilteredObjectByValuePath(
        this.offers,
        "product.id",
        String(productId)
      );
    },
    getUserParamsChanged() {
      const delivery = window.dataApp.delivery;
      const DELIVERY_TYPES = {
        RESERVE: "Резерв",
        PVZ: "ПВЗ",
        SELF_PICKUP: "Самовывоз",
        EXPRESS: "Срочная доставка",
        COURIER: "КС",
      };

      // Инициализация значений по умолчанию
      let deliveryBlock = Object.values(DELIVERY_TYPES).reduce((acc, type) => {
        acc[`${type}`] = "Нет";
        return acc;
      }, {});

      // Обновление значений доставки, если опции видимы
      if (this.isDeliveryOptionsVisible) {
        if (this.canReserve) {
          deliveryBlock[DELIVERY_TYPES.RESERVE] = delivery.reserve_time_days;
        }

        if (!this.notAvailable && this.canAddCart) {
          deliveryBlock[DELIVERY_TYPES.PVZ] = Number(delivery.time_days);
        }

        if (this.isCityPresence && this.getSelectedOffer.addition) {
          deliveryBlock[DELIVERY_TYPES.SELF_PICKUP] = Number(
            delivery.time_days
          );
        }

        if (this.getSelectedOffer.fast_delivery) {
          deliveryBlock[DELIVERY_TYPES.EXPRESS] = Number(
            delivery.fast_time_days
          );
        }

        if (delivery.courier_time_days && this.canAddCart) {
          deliveryBlock[DELIVERY_TYPES.COURIER] = Number(
            delivery.courier_time_days
          );
        }
      }

      const isOfferSelected =
        (this.isSelected.size || this.selectedOfferId != null) &&
        this.getSelectedOffer?.kod_torgovogo_predlozheniya;

      const productCode = isOfferSelected
        ? this.getSelectedOffer?.kod_torgovogo_predlozheniya
        : this.getSelectedProduct?.productCode;

      const clientId = this.getSafeYandexClientId;
      const currentDate = new Date();
      const currentCity = delivery.current_city;

      // Формируем итоговый объект
      return {
        [productCode]: {
          [currentCity]: {
            [JSON.stringify(deliveryBlock)]: {
              [currentDate]: {
                clientId,
              },
            },
          },
        },
      };
    },
    selectOfferById(offerId) {
      document.dispatchEvent(
        new CustomEvent("get-salon-event", {
          detail: { offerId: offerId },
        })
      );

      Object.keys(this.offers).forEach((key) => {
        this.offers[key]["selected"] = offerId === this.offers[key]["id"];
      });
      document.dispatchEvent(
        new CustomEvent("selectOfferByIdEvent", {
          detail: {
            params: this.getUserParamsChanged(),
          },
        })
      );
      setOfferUrl(this.getSelectedProduct.code, offerId);
    },
    getOfferIdFromSize(sizeId) {
      const filter = [
        {
          name: "product.id",
          value: objectByString(this.getSelectedOffer, "product.id"),
        },
        {
          name: "size.id",
          value: sizeId,
        },
      ];
      const propsObj = objectByString(this.getSelectedOffer, "props");
      if (propsObj !== undefined) {
        const props = Object.entries(propsObj);
        for (const [key, item] of props) {
          filter.push({
            name: "props." + key + ".value.id",
            value: objectByString(item, "value.id"),
          });
        }
      }

      let offersClone = Object.assign({}, this.offers);
      let objects = filterObject(offersClone, filter);
      let offer = Object.values(objects)[0];

      return offer?.id;
    },
    setSize(sizeId) {
      this.isSelected.size = true;

      this.selectOfferById(this.getOfferIdFromSize(sizeId));
    },
    setOffer(offerId) {
      this.isSelected.size = true;

      this.selectOfferById(offerId);
    },
    getOfferIdFromProperty(propertyName, propertyValue) {
      const filter = [
        {
          name: "product.id",
          value: objectByString(this.getSelectedOffer, "product.id"),
        },
        {
          name: "size.id",
          value: objectByString(this.getSelectedOffer, "size.id"),
        },
      ];
      const propsObj = objectByString(this.getSelectedOffer, "props");
      if (propsObj !== undefined) {
        const props = Object.entries(propsObj);

        for (const [key, item] of props) {
          if (key === propertyName) {
            filter.push({
              name: "props." + key + ".value.id",
              value: objectByString(propertyValue, "id"),
            });
          } else {
            filter.push({
              name: "props." + key + ".value.id",
              value: objectByString(item, "value.id"),
            });
          }
        }
      }

      let offersClone = Object.assign({}, this.offers);
      let objects = filterObject(offersClone, filter);
      let offer = Object.values(objects)[0];

      return offer?.id;
    },
    selectProperty(propertyName, propertyValue) {
      this.isSelected[propertyName] = true;

      this.selectOfferById(
        this.getOfferIdFromProperty(propertyName, propertyValue)
      );
    },
    async addToCart(event) {
      if (!this.isAllParamSelected()) {
        document.dispatchEvent(new Event("check-selected-params"));
        document.dispatchEvent(new Event("show-error-no-selected"));

        return false;
      }

      if (event.target.classList.contains("running")) {
        return false;
      }

      event.target.classList.toggle("running");

      const response = await axios({
        method: "post",
        url: "/local/ajax/basket.php",
        params: {
          action: "add",
          id: this.getSelectedOffer.id,
        },
      });

      if (response.data.status === "ok") {
        let counter = document.querySelector(".js-basket-counter");
        if (counter) {
          counter.innerHTML = response.data.count;
          if (response.data.count > 0) {
            counter.classList.remove("none");
          } else {
            counter.classList.add("none");
          }
        }
        this.basket.list = response?.data?.arBasket;
        this.basket.count = this.basket.list?.length;

        window.BX.onCustomEvent("OnBasketChange");
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "add_to_cart",
          ecommerce: {
            currency: "RUB",
            items: [
              {
                item_name: this.getSelectedProduct.name,
                item_id:
                  this.getSelectedOffer?.kod_torgovogo_predlozheniya.replaceAll(
                    "-",
                    ""
                  ),
                group_id: this.getSelectedOffer.product.id,
                price: this.getSelectedOffer.price?.current_number + ".00",
                item_brand: this.getSelectedProduct.props?.brand ?? "",
                item_category: this.getSelectedProduct.section?.name ?? "",
                quantity: "1",
              },
            ],
          },
        });
      } else {
        this.errors.basket = true;
      }

      event.target.classList.toggle("running");
    },
    selectOfferBySizeGuid(sizeGuid) {
      const filter = [
        {
          name: "product.id",
          value: objectByString(this.getSelectedOffer, "product.id"),
        },
        {
          name: "size.xml_id",
          value: sizeGuid,
        },
      ];

      let offersClone = Object.assign({}, this.offers);
      let objects = filterObject(offersClone, filter);
      let offer = Object.values(objects)[0];
      this.selectOfferById(offer.id);
    },
    loadWishList() {
      document.addEventListener("onWishListVueCheck", () => {
        this.$patch((state) => {
          state.wishList = Object.values(window.vueData.wishList);
        });
      });
    },
    addWishList(productId, offerId = null) {
      let wishList = window.BX.WishList();
      wishList.updateWishList(productId, offerId);
      this.updateWishList(productId);
    },
    updateWishList(productId) {
      if (productId) {
        let wishList = this.getWishList;
        const found = wishList.find((wishItem) => {
          return parseInt(wishItem) === parseInt(productId);
        });

        if (found) {
          wishList = wishList.filter((wishItem) => {
            return parseInt(wishItem) !== parseInt(productId);
          });
        } else {
          wishList.push(productId);
        }
        this.wishList = wishList;
      }
    },
    setMainActiveSlide(index) {
      this.mainSlider.activeSlideIndex = index;
    },
  },
  getters: {
    getSafeYandexClientId() {
      const match = document.cookie.match(/(^| )_ym_uid=([^;]+)/);
      const yaId = match ? match[2] : null;

      if (yaId) {
        return yaId;
      }

      let localId = localStorage.getItem("user_permanent_id");
      if (!localId) {
        localId =
          "generated_" +
          Date.now() +
          "_" +
          Math.random().toString(36).substring(2, 9);
        localStorage.setItem("user_permanent_id", localId);
      }

      return localId;
    },
    getYandexClientId() {
      const match = document.cookie.match(/(^| )_ym_uid=([^;]+)/);
      return match ? match[2] : null;
    },
    getSelectedProduct(state) {
      return (
        getObjectElementByValuePath(state.products, "selected", true) ??
        state.product
      );
    },
    getProductId(state) {
      return state.getSelectedProduct?.id;
    },
    getTitle(state) {
      return state.getSelectedProduct?.name ?? "Идет загрузка...";
    },
    getColors(state) {
      let data = {};
      Object.entries(state.products).forEach(([, product]) => {
        if (product?.color?.id !== undefined) {
          data[objectByString(product, "id")] = product.color;
        }
      });

      return data;
    },
    getProductBrand(state) {
      return objectByString(state.getSelectedProduct, "props.brand");
    },
    getProductLineykaXmlId(state) {
      return objectByString(state.getSelectedProduct, "props.lineyka");
    },
    getProductVidTovara(state) {
      return objectByString(state.getSelectedProduct, "props.vid_tovara");
    },
    isEmptyOffers(state) {
      let offers = state.getOffersForProduct;
      return Object.keys(offers).length === 0;
    },
    getSelectedOffer(state) {
      return getObjectElementByValuePath(state.offers, "selected", true);
    },
    getOffersId(state) {
      let offersId = [];
      Object.values(state.offers).forEach((offer) => {
        if (offer.product.id === state.getSelectedProduct.id) {
          offersId.push(Number(offer.id));
        }
      });

      return offersId;
    },
    getSelectedSize(state) {
      if (this.getSelectedOffer === undefined) {
        return false;
      }
      return objectByString(state.getSelectedOffer, "size.id");
    },
    getActions() {
      return this.getSelectedOffer?.actions;
    },
    getWishList(state) {
      return state.wishList;
    },
    getOffersForProduct(state) {
      return getFilteredObjectByValuePath(
        state.offers,
        "product.id",
        String(state.getProductId)
      );
    },
    getMinimalPriceFormatted(state) {
      return state.getSelectedOffer?.prices?.discount?.value > 0
        ? state.getSelectedOffer?.prices?.discount?.formatted
        : state.getSelectedOffer?.prices?.base?.formatted;
    },
    isDiscountExists(state) {
      return state.getSelectedOffer?.prices?.discount_size?.percent > 0;
    },
    getDiscountPricePercent(state) {
      return state.getSelectedOffer?.prices?.discount_size?.percent;
    },
    getMinimalPrice(state) {
      return state.getSelectedOffer?.prices?.discount?.value > 0
        ? state.getSelectedOffer?.prices?.discount?.value
        : state.getSelectedOffer?.prices?.base?.value;
    },
    getBasePrice(state) {
      return state.getSelectedOffer?.prices?.base?.formatted;
    },
    getMainPicture(state) {
      return state.getSelectedProduct.media[0]?.sizes;
    },
    isDolyami(state) {
      return state.getMinimalPrice < 40000;
    },
    isPaylate(state) {
      return state.getMinimalPrice >= 40000;
    },
    getInstallmentMinPrice() {
      if (this.installments?.tinkoff[0] === undefined) {
        return false;
      }

      return this.installments?.tinkoff[0]["amount"];
    },
    getDolyamiLink(state) {
      return "/local/ajax/dolyame_calc.php?price=" + state.getMinimalPrice;
    },
    getDolyamiData(state) {
      return state.installments.tinkoff;
    },
    getProductUtp(state) {
      return state.getSelectedProduct?.props.utp_items ?? {};
    },
    getFeaturesList(state) {
      return objectByString(state.getSelectedProduct, "property_table");
    },
    getOfferFeaturesList(state) {
      if (!state.getSelectedOffer) {
        return;
      }

      return objectByString(state.getSelectedOffer, "property_table");
    },
    hasFeatures(state) {
      if (state.getFeaturesList === undefined) {
        return false;
      } else {
        return Object.keys(state.getFeaturesList).length > 0;
      }
    },
    getBadgesList(state) {
      return objectByString(state.getSelectedProduct, "props.badges");
    },
    getDelivery(state) {
      if (Object.keys(state.delivery.result).length > 0) {
        return state.delivery.result;
      } else {
        return {};
      }
    },
    getDeliveryAttempts(state) {
      return state.delivery.attempts;
    },
    getDocuments(state) {
      return state.getSelectedProduct.documents;
    },
    getSpecial(state) {
      return state.special;
    },
    isFccAvailable(state) {
      return objectByString(state.getSelectedProduct, "props.fcc");
    },
    isVideoCallAvailable(state) {
      return (
        objectByString(state.getSelectedProduct, "props.can_video_call") === "Y"
      );
    },
    isIpraAvailable(state) {
      return objectByString(state.getSelectedProduct, "props.ipra") === "Y";
    },
    getMedia(state) {
      return state.getSelectedProduct.media;
    },
    getMainSlideIndex(state) {
      return state.mainSlider.activeSlideIndex;
    },
    isShowSizeMap(state) {
      return (
        isShowSizeMap(state) &&
        state.getSelectedProduct?.section.code === "ortopedicheskaya-obuv"
      );
    },
    getSizePicture(state) {
      return state.getSelectedProduct.sizePicture?.url?.default ?? "";
    },
    getSizeMapRows(state) {
      return objectByString(state.getSelectedProduct, "sizeMap.rows");
    },
    getSizeGuid(state) {
      return objectByString(state.getSelectedOffer, "size.xml_id");
    },
    getProperties(state) {
      let data = {};
      if (state.getSelectedOffer === undefined) {
        return false;
      }

      const propsObj = objectByString(state.getSelectedOffer, "props");
      if (propsObj === undefined) {
        return false;
      }

      const props = Object.entries(propsObj);
      let filter = [
        {
          name: "product.id",
          value: objectByString(state.getSelectedOffer, "product.id"),
        },
        {
          name: "size.id",
          value: objectByString(state.getSelectedOffer, "size.id"),
        },
      ];
      for (const [key, item] of props) {
        filter.push({
          name: "props." + key + ".value.id",
          value: objectByString(item, "value.id"),
        });
      }

      const objects = filterSameObject(state.offers, filter);

      Object.entries(objects).forEach((offer) => {
        const [, offerData] = offer;

        if (offerData.props === undefined) {
          return;
        }

        Object.entries(offerData?.props).forEach((prop) => {
          // eslint-disable-next-line no-unused-vars
          const [propCode, propData] = prop;
          const propName = propData?.name;
          if (data[propCode] === undefined) {
            data[propCode] = {
              name: propName,
              values: {},
            };
          }

          const propId = propData?.value?.id;
          if (
            propId &&
            propData?.value &&
            data[propCode]["values"][propId] === undefined
          ) {
            data[propCode]["values"][propId] = propData?.value;
          }
        });
      });

      return data;
    },
    getAllOffersProp(state) {
      let data = {};
      Object.entries(state.offers).forEach((offer) => {
        const [, offerData] = offer;

        if (offerData.props === undefined) {
          return;
        }

        Object.entries(offerData?.props).forEach((prop) => {
          // eslint-disable-next-line no-unused-vars
          const [propCode, propData] = prop;
          const propName = propData?.name;
          if (data[propCode] === undefined) {
            data[propCode] = {
              name: propName,
              values: {},
            };
          }

          const propId = propData?.value?.id;
          if (
            propId &&
            propData?.value &&
            data[propCode]["values"][propId] === undefined
          ) {
            data[propCode]["values"][propId] = propData?.value;
          }
        });
      });

      return data;
    },
    isServiceOrder() {
      return Number(this.getSelectedProduct?.service?.id) > 0;
    },
    isShippingRestriction() {
      return this.getSelectedProduct?.shipping?.xml_id === "Y";
    },
    isInBasket() {
      return this.basket.list.includes(Number(this.getSelectedOffer?.id));
    },
    isReserveOnly() {
      return (
        Number(this.getSelectedProduct?.props?.reserve_only_available) === 1
      );
    },
    isCollagen() {
      return (
        this.getSelectedProduct?.code?.indexOf("bad-collagen") !== -1 ||
        this.getSelectedProduct?.code?.indexOf("zhele-collagen") !== -1
      );
    },
    isReserved() {
      return this.isShippingRestriction === false || this.isCollagen === true;
    },
    canAddCart() {
      return (
        this.isServiceOrder === false &&
        this.isShippingRestriction === false &&
        this.isReserveOnly === false &&
        this.getSelectedOffer?.addition === true
      );
    },
    canReserve() {
      return (
        (this.getSelectedOffer?.reserve === true &&
          this.isReserved === true &&
          this.isServiceOrder === false) ||
        this.isReserveOnly
      );
    },
    notAvailable() {
      return (
        !this.getSelectedOffer?.price?.current ||
        (this.isCollagen && !this.canReserve && !this.canAddCart)
      );
    },
    isRichContent(state) {
      return state.getSelectedProduct?.rich_content;
    },
    getRichContentImg(state) {
      const { rich_content } = state.getSelectedProduct;
      return "/upload/" + rich_content.subdir + "/" + rich_content.file_name;
    },
    isCityPresence() {
      return window.isCityPresenceApp;
    },
    isDeliveryOptionsVisible(state) {
      return (
        !state.notAvailable &&
        state.isShippingRestriction === false &&
        state.isServiceOrder === false
      );
    },
  },
});
